import React, { useState, useRef, useEffect } from "react";
import clsx from "clsx";
import AppIcon from "@/components/AppIcon";
import AppButton from "@/components/AppButton";
import { FORM_ELEMENT_HEIGHT } from "@/constants/styles";

interface AppDropdownProps {
  label: string;
  items: { label: string; value: number | string }[];
  value?: string | number;
  onChange: (value: string | number) => void;
  disabled?: boolean;
  className?: string;
  position?: "up" | "down" | "auto";
}

const AppDropdown: React.FC<AppDropdownProps> = ({ label, items, value, onChange, disabled, className, position = "down" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [dropDirection, setDropDirection] = useState<"up" | "down">(position === "auto" ? "down" : position);

  // Toggle dropdown state
  const handleToggle = () => setIsOpen((prev) => !prev);

  // Close dropdown when clicked outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (position === "auto" && isOpen && dropdownRef.current) {
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      // Перевіряємо відстань від елемента до низу вікна
      const bottomSpace = windowHeight - dropdownRect.bottom;
      const DROPDOWN_HEIGHT = 250; // Приблизна висота випадаючого списку
      
      if (bottomSpace < DROPDOWN_HEIGHT) {
        setDropDirection("up");
      } else {
        setDropDirection("down");
      }
    }
  }, [isOpen, position]);

  // Dropdown classes
  const dropdownClasses = clsx(
    "absolute w-fit min-w-full origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 z-[90]",
    {
      "mt-2": dropDirection === "down",
      "bottom-full mb-2": dropDirection === "up",
      "opacity-0 scale-95": !isOpen,
      "opacity-100 scale-100": isOpen,
    },
    "transition transform ease-in-out duration-100"
  );

  return (
    <div ref={dropdownRef} className="relative inline-block text-left w-full">
      <AppButton
        variant="dropdown"
        className={clsx(
          "flex items-center justify-between gap-x-1.5 capitalize",
          FORM_ELEMENT_HEIGHT,
          className
        )}
        onClick={handleToggle}
        disabled={disabled}
      >
        <span className="truncate">{label}</span>
        <AppIcon name="chevronDown" className="text-gray-400 flex-shrink-0" />
      </AppButton>
      {isOpen && (
        <div className={dropdownClasses} style={{ maxHeight: "200px", overflowY: "auto" }}>
          <ul className="py-1">
            {items.map((item) => (
              <li key={item.value}>
                <button
                  className={clsx(
                    "block w-full px-4 py-2 text-sm text-left hover:bg-gray-100 capitalize",
                    value === item.value ? "bg-gray-100 text-gray-900" : "text-gray-700",
                    className
                  )}
                  onClick={() => {
                    setIsOpen(false);
                    onChange(item.value);
                  }}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AppDropdown;